import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, CheckCircle2, TrendingUp, Info } from "lucide-react";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS = {
  prejuizo: { label: "Abaixo do custo", cls: "bg-red-50 text-red-800 border-red-200", Icon: AlertTriangle },
  apertada: { label: "Margem apertada", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle },
  saudavel: { label: "Saudável", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  acima: { label: "Acima da referência", cls: "bg-sky-50 text-sky-800 border-sky-200", Icon: TrendingUp },
  sem_dados: { label: "Dados insuficientes", cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: Info },
};

function Row({ label, praticado, referencia, testid }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-center border-b border-slate-100 pb-2" data-testid={testid}>
      <p className="text-sm text-slate-600">{label}</p>
      <p className="font-mono-num text-sm font-semibold text-slate-900 text-right" data-testid={`${testid}-praticado`}>
        {praticado != null ? brl(praticado) : "—"}
        <span className="block text-[10px] font-normal text-slate-400">você pratica</span>
      </p>
      <p className="font-mono-num text-sm font-semibold text-sky-700 text-right" data-testid={`${testid}-referencia`}>
        {referencia != null ? brl(referencia) : "—"}
        <span className="block text-[10px] font-normal text-slate-400">referência GEPR</span>
      </p>
    </div>
  );
}

export function CostsDiagnosis() {
  const [diag, setDiag] = useState(null);

  useEffect(() => {
    api.get("/costs/diagnosis").then((r) => setDiag(r.data)).catch(() => {});
  }, []);

  if (!diag || !diag.configured) return null;

  if (!diag.has_data) {
    return (
      <Card className="border-slate-200" data-testid="diag-card">
        <CardContent className="p-4 space-y-2">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Activity className="h-4 w-4 text-sky-600" /> Como está minha precificação?
          </p>
          <p className="text-sm text-slate-500" data-testid="diag-empty">
            Ainda não há orçamentos para analisar. Crie orçamentos na{" "}
            <Link to="/app/calculadora" className="text-sky-700 hover:underline" data-testid="diag-cta-calc">Calculadora</Link>{" "}
            e o GEPR compara automaticamente os preços que você pratica com seus custos reais.
          </p>
        </CardContent>
      </Card>
    );
  }

  const st = STATUS[diag.status] || STATUS.sem_dados;
  const ref = diag.referencia || {};
  const pr = diag.praticado || {};

  return (
    <Card className={`border ${st.cls.split(" ").find((c) => c.startsWith("border-")) || "border-slate-200"}`} data-testid="diag-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Activity className="h-4 w-4 text-sky-600" /> Como está minha precificação?
          </p>
          <Badge variant="outline" className={st.cls} data-testid="diag-status">
            <st.Icon className="h-3 w-3 mr-1" /> {st.label}
          </Badge>
        </div>
        <p className="text-[11px] text-slate-400">Baseado nos seus últimos {diag.quotes_analisados} orçamento(s) comparados aos seus custos operacionais. Dados objetivos, sem julgamento.</p>

        <div className="space-y-2">
          <Row label="Hora técnica" praticado={pr.valor_hora} referencia={ref.valor_hora_sugerido} testid="diag-hora" />
          {(pr.valor_km != null || ref.valor_km_sugerido != null) && (
            <Row label="Km de deslocamento" praticado={pr.valor_km} referencia={ref.valor_km_sugerido} testid="diag-km" />
          )}
          <div className="grid grid-cols-3 gap-2 items-center pb-1" data-testid="diag-margem">
            <p className="text-sm text-slate-600">Margem</p>
            <p className="font-mono-num text-sm font-semibold text-slate-900 text-right" data-testid="diag-margem-real">
              {pr.margem_real_pct != null ? `~${pr.margem_real_pct}%` : "—"}
              <span className="block text-[10px] font-normal text-slate-400">real estimada</span>
            </p>
            <p className="font-mono-num text-sm font-semibold text-sky-700 text-right" data-testid="diag-margem-desejada">
              {ref.margem_pct != null ? `${ref.margem_pct}%` : "—"}
              <span className="block text-[10px] font-normal text-slate-400">desejada</span>
            </p>
          </div>
          {(diag.impacto_deslocamento_pct != null || diag.impacto_materiais_pct != null) && (
            <div className="grid grid-cols-2 gap-2 text-center" data-testid="diag-impacts">
              {diag.impacto_deslocamento_pct != null && (
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="font-mono-num text-sm font-semibold text-slate-900" data-testid="diag-impacto-deslocamento">{diag.impacto_deslocamento_pct}%</p>
                  <p className="text-[10px] text-slate-500">impacto médio do deslocamento no preço</p>
                </div>
              )}
              {diag.impacto_materiais_pct != null && (
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="font-mono-num text-sm font-semibold text-slate-900" data-testid="diag-impacto-materiais">{diag.impacto_materiais_pct}%</p>
                  <p className="text-[10px] text-slate-500">impacto médio de materiais/peças no preço</p>
                </div>
              )}
            </div>
          )}
        </div>

        {diag.total_abaixo_custo > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1.5" data-testid="diag-abaixo">
            <p className="text-xs font-semibold text-red-800">Serviços com hora abaixo do seu custo operacional ({diag.total_abaixo_custo})</p>
            {diag.servicos_abaixo_custo.map((s, i) => (
              <p key={i} className="text-xs text-red-700 font-mono-num" data-testid={`diag-abaixo-${i}`}>
                {s.number} · {s.service_type} — hora praticada {brl(s.valor_hora)} (custo: {brl(ref.custo_hora || 0)})
              </p>
            ))}
          </div>
        )}

        {diag.insights?.length > 0 && (
          <ul className="space-y-1.5" data-testid="diag-insights">
            {diag.insights.map((ins, i) => (
              <li key={i} className="text-xs text-slate-600 flex gap-1.5" data-testid={`diag-insight-${i}`}>
                <span className="text-sky-500">•</span><span>{ins}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
